import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import JsonapiClient from '@holidayextras/jsonapi-client';
import DatePicker from 'react-datepicker';
import { countries } from 'country-data';
import {
  Modal,
  Form,
  Grid,
  Button,
  Icon,
  Input,
  Dropdown,
  Message,
  Tab,
  Checkbox,
} from 'semantic-ui-react';

import HashtagsInput from '../hashtags-input';
import { DYZIO_CURRENCIES } from '../../utils/helpers/currencies';
import { transformHashtagstIntoArray } from '../../utils/hashtags-utils';
import { getInfluencerFromOrganisations } from '../../utils/helper-functions';
import { authService } from '../../utils/auth-service';
import createNewCampaign from '../../utils/create-new-campaign';
import inviteCreatorToCampaign from '../../utils/invite-creator-to-campaign';

const mutedStyle = { color: 'gray' };
const dropdownStyle = { marginTop: 8, marginBottom: 8 };
const paneStyle = { border: 0, padding: 0, boxShadow: 'none' };
const creatorListStyle = {
  maxHeight: 280,
  overflowY: 'auto',
  borderTop: '1px solid #f0f0f0',
};
const creatorRowStyle = { padding: '8px 0', borderBottom: '1px solid #f5f5f5' };
const masterRowStyle = {
  padding: '8px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const RequiredLabel = () => <span style={{ color: 'red' }}>*</span>;

const COUNTRY_OPTIONS = [
  {
    text: '<<< No Country >>>',
    style: mutedStyle,
    key: 'no-country',
    value: null,
  },
  ..._(countries.all)
    .reject((c) => _.isEmpty(c.emoji))
    .map((c) => ({
      text: `${c.emoji} ${c.name}`,
      key: c.alpha2,
      value: c.alpha2,
    }))
    .value(),
];

const safeDateAt15 = (date) =>
  date && moment(date).isValid()
    ? moment(date).set({ hour: 15, minute: 15 })
    : null;

const buildCreatorFromDiscussion = (discussion) => {
  const influencer =
    discussion.influencer ||
    getInfluencerFromOrganisations(discussion.organisations);
  const name = discussion.name || _.get(influencer, 'name') || '';
  const email = _.get(influencer, 'email') || '';
  if (!name && !email) return null;
  const handle =
    _.get(influencer, 'primarySocialHandle') ||
    _.get(influencer, 'instagram') ||
    _.get(influencer, 'tiktok') ||
    _.get(influencer, 'youtube') ||
    _.get(influencer, 'twitter') ||
    '';
  return {
    id: discussion.id,
    name,
    email,
    handle,
    instagram: _.get(influencer, 'instagram') || '',
    primarySocialHandle: _.get(influencer, 'primarySocialHandle') || '',
    influencerId: _.get(influencer, 'id'),
  };
};

const buildInviteFromCreator = (creator) => ({
  name: creator.name,
  email: creator.email,
  role: 'INFLUENCER',
  instagram: creator.instagram,
  primarySocialHandle: creator.primarySocialHandle,
  id: creator.influencerId,
});

export default class DuplicateCampaignModal extends Component {
  constructor(props) {
    super(props);
    const { sourceCampaign } = props;
    const sourceName = _.get(sourceCampaign, 'name', '');
    this.state = {
      name: sourceName ? `${sourceName} (duplicate)` : '',
      brand: _.get(sourceCampaign, 'brand') || '',
      budget: _.get(sourceCampaign, 'budget', ''),
      currency: _.get(sourceCampaign, 'currency') || null,
      hashtags: _.get(sourceCampaign, 'hashtags') || '',
      country: _.get(sourceCampaign, 'country') || null,
      liveDate: sourceCampaign.liveDate
        ? moment(sourceCampaign.liveDate)
        : null,
      endDate: sourceCampaign.endDate ? moment(sourceCampaign.endDate) : null,
      creators: _(_.get(sourceCampaign, 'discussions', []))
        .map(buildCreatorFromDiscussion)
        .compact()
        .value(),
      selectedCreatorIds: new Set(),
      creatorSearch: '',
      submitting: false,
      error: null,
      noMentionsWarned: false,
    };

    this.client = new JsonapiClient(process.env.REACT_APP_API_BASE_URL, {
      header: { Authorization: `Bearer ${authService.getToken()}` },
    });
  }

  onFieldChanged = (event, { name, value }) => this.setState({ [name]: value });
  onLiveDateChange = (date) => this.setState({ liveDate: date });
  onEndDateChange = (date) => this.setState({ endDate: date });

  onToggleCreator = (id) =>
    this.setState((prev) => {
      const next = new Set(prev.selectedCreatorIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedCreatorIds: next };
    });

  onToggleAll = () =>
    this.setState((prev) => {
      const allSelected =
        prev.creators.length > 0 &&
        prev.selectedCreatorIds.size === prev.creators.length;
      return {
        selectedCreatorIds: allSelected
          ? new Set()
          : new Set(_.map(prev.creators, (c) => c.id)),
      };
    });

  isSubmitDisabled = () => {
    const {
      name,
      hashtags,
      budget,
      liveDate,
      endDate,
      submitting,
    } = this.state;
    return (
      submitting ||
      _.isEmpty(name) ||
      _.isEmpty(hashtags) ||
      _.isEmpty(_.toString(budget)) ||
      !liveDate ||
      !endDate
    );
  };

  hasMention = () =>
    _.some(_.split(this.state.hashtags, ','), (t) =>
      _.startsWith(_.trim(t), '@'),
    );

  onSubmit = () => {
    if (!this.hasMention() && !this.state.noMentionsWarned) {
      this.setState({ noMentionsWarned: true });
      return undefined;
    }
    return this.doSubmit();
  };

  doSubmit = async () => {
    if (this.inflight) return;
    this.inflight = true;

    const {
      name,
      brand,
      budget,
      currency,
      hashtags,
      country,
      liveDate,
      endDate,
      creators,
      selectedCreatorIds,
    } = this.state;
    const { jsonAPIClient, userInfo, organisation, sourceCampaign } =
      this.props;

    this.setState({ submitting: true, error: null });

    let newCampaignId;
    try {
      newCampaignId = await createNewCampaign({
        name,
        brand,
        budget,
        currency,
        hashtags,
        country,
        liveDate,
        endDate,
        campaignType: _.get(sourceCampaign, 'campaignType', 'AUTOMATED'),
        scanUnauthorisedStories: _.get(organisation, 'isStickerScannerEnabled'),
        useEstimatedReachAndImpressions: false,
        jsonAPIClient,
        userInfo,
        organisation,
        skipRedirect: true,
      });
    } catch (e) {
      // Create failed — no campaign exists, surface the error and let the user retry.
      this.inflight = false;
      this.setState({
        submitting: false,
        error: _.get(
          e,
          'message',
          'Could not duplicate the campaign. Please try again.',
        ),
      });
      return;
    }

    // Campaign exists from here on. Invite failures are logged but do NOT abort
    // the redirect — otherwise the user sees an error and re-clicks Duplicate,
    // creating a second campaign.
    const selected = _.filter(creators, (c) => selectedCreatorIds.has(c.id));
    if (!_.isEmpty(selected)) {
      await Promise.all(
        _.map(selected, (creator) =>
          inviteCreatorToCampaign({
            client: this.client,
            campaignId: newCampaignId,
            campaignName: name,
            organisationId: _.get(userInfo, 'organisationId'),
            invitation: buildInviteFromCreator(creator),
          }).catch((err) => {
            console.error(
              'Failed to invite creator to duplicated campaign',
              err,
            ); // eslint-disable-line no-console
            return null;
          }),
        ),
      );
    }

    window.location = `/campaign/${newCampaignId}`;
  };

  renderDetailsPane() {
    const {
      name,
      brand,
      budget,
      currency,
      hashtags,
      country,
      liveDate,
      endDate,
    } = this.state;
    const safeLive = safeDateAt15(liveDate);
    const safeEnd = safeDateAt15(endDate);

    return (
      <Form>
        <Form.Field>
          <label>
            Campaign name
            <RequiredLabel />
          </label>
          <Form.Input
            autoFocus
            name="name"
            value={name}
            onChange={this.onFieldChanged}
          />
        </Form.Field>

        <Form.Group widths="equal">
          <Form.Field>
            <label>Brand</label>
            <Form.Input
              name="brand"
              value={brand}
              onChange={this.onFieldChanged}
            />
          </Form.Field>

          <Form.Field>
            <label>Country (optional)</label>
            <Dropdown
              fluid
              search
              selection
              name="country"
              placeholder="e.g United Kingdom"
              value={country}
              options={COUNTRY_OPTIONS}
              onChange={this.onFieldChanged}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field>
            <label>Currency</label>
            <Dropdown
              fluid
              search
              selection
              name="currency"
              style={dropdownStyle}
              placeholder="Select currency"
              value={currency}
              options={DYZIO_CURRENCIES}
              onChange={this.onFieldChanged}
            />
          </Form.Field>

          <Form.Field>
            <label>
              Total Budget
              <RequiredLabel />
            </label>
            <Form.Input
              type="number"
              name="budget"
              value={budget}
              onChange={this.onFieldChanged}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field>
            <label>
              Live Date
              <RequiredLabel />
            </label>
            <DatePicker
              onChange={this.onLiveDateChange}
              selected={safeLive}
              dateFormat="MMM Do YYYY"
              selectsStart
              endDate={safeEnd}
              maxDate={safeEnd}
              popperPlacement="top-start"
            />
          </Form.Field>

          <Form.Field>
            <label>
              End Date
              <RequiredLabel />
            </label>
            <DatePicker
              onChange={this.onEndDateChange}
              selected={safeEnd}
              dateFormat="MMM Do YYYY"
              selectsEnd
              startDate={safeLive}
              minDate={safeLive}
              popperPlacement="top-start"
            />
          </Form.Field>
        </Form.Group>

        <HashtagsInput
          onValueChanged={this.onFieldChanged}
          campaignType="AUTOMATED"
          hashtags={transformHashtagstIntoArray(hashtags)}
        />
      </Form>
    );
  }

  renderCreatorsPane() {
    const { creators, selectedCreatorIds, creatorSearch } = this.state;

    if (_.isEmpty(creators)) {
      return (
        <p style={{ ...mutedStyle, padding: '24px 0', textAlign: 'center' }}>
          No creators on the source campaign.
        </p>
      );
    }

    const q = _.trim(_.toLower(creatorSearch));
    const visible = q
      ? _.filter(creators, c => (
        _.includes(_.toLower(c.name || ''), q)
        || _.includes(_.toLower(c.email || ''), q)
        || _.includes(_.toLower(c.handle || ''), q)
      ))
      : creators;

    const total = creators.length;
    const selectedCount = selectedCreatorIds.size;
    const allSelected = selectedCount === total;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <div>
        <Input
          fluid
          icon="search"
          iconPosition="left"
          placeholder="Search by name, email, or @handle"
          value={creatorSearch}
          onChange={(e, { value }) => this.setState({ creatorSearch: value })}
          style={{ marginBottom: 8 }}
        />
        <div style={masterRowStyle}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={this.onToggleAll}
            label={
              <label>
                <strong>Select all</strong>
              </label>
            }
          />
          <span style={mutedStyle}>
            {selectedCount} of {total} selected
          </span>
        </div>
        <div style={creatorListStyle}>
          {_.isEmpty(visible) ? (
            <p style={{ ...mutedStyle, padding: '24px 0', textAlign: 'center' }}>
              No creators match “{creatorSearch}”.
            </p>
          ) : (
            _.map(visible, c => (
              <div key={c.id} style={creatorRowStyle}>
                <Checkbox
                  checked={selectedCreatorIds.has(c.id)}
                  onChange={() => this.onToggleCreator(c.id)}
                  label={
                    <label>
                      <strong>{c.name || c.email || 'Unnamed creator'}</strong>
                      {c.email && <span style={mutedStyle}> — {c.email}</span>}
                      {c.handle && (
                        <span style={mutedStyle}> · @{c.handle}</span>
                      )}
                    </label>
                  }
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  render() {
    const { submitting, error, creators, selectedCreatorIds } = this.state;
    const { onClose } = this.props;
    const showNoMentionsWarning =
      this.state.noMentionsWarned && !this.hasMention();
    const creatorsTabLabel = `Creators (${selectedCreatorIds.size}/${creators.length})`;

    const panes = [
      {
        menuItem: 'Details',
        render: () => (
          <Tab.Pane attached={false} style={paneStyle}>
            {this.renderDetailsPane()}
          </Tab.Pane>
        ),
      },
      {
        menuItem: creatorsTabLabel,
        render: () => (
          <Tab.Pane attached={false} style={paneStyle}>
            {this.renderCreatorsPane()}
          </Tab.Pane>
        ),
      },
    ];

    return (
      <Modal
        open
        closeOnEscape
        closeOnDimmerClick={!submitting}
        onClose={submitting ? undefined : onClose}
        size="small"
      >
        <Modal.Header>Duplicate campaign</Modal.Header>
        <Modal.Content scrolling>
          <Tab menu={{ secondary: true, pointing: true }} panes={panes} />
          {showNoMentionsWarning && (
            <Message warning>
              <Message.Header>
                <Icon name="warning circle" />
                No @mention tracked
              </Message.Header>
              <p>
                Your campaign has zero @mentions. Click{' '}
                <strong>Duplicate campaign</strong> again to proceed anyway.
              </p>
            </Message>
          )}
          {error && (
            <Message negative>
              <Message.Header>
                <Icon name="warning circle" />
                Couldn’t duplicate
              </Message.Header>
              <p>{error}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <Button
                  fluid
                  basic={false}
                  disabled={submitting}
                  onClick={onClose}
                >
                  <Icon name="close" />
                  Cancel
                </Button>
              </Grid.Column>
              <Grid.Column width={8}>
                <Button
                  fluid
                  basic={false}
                  color="pink"
                  disabled={this.isSubmitDisabled()}
                  onClick={this.onSubmit}
                >
                  <Icon name="copy" />
                  {submitting ? 'Duplicating…' : 'Duplicate campaign'}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Actions>
      </Modal>
    );
  }
}

DuplicateCampaignModal.propTypes = {
  sourceCampaign: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  organisation: PropTypes.object,
};

DuplicateCampaignModal.defaultProps = {
  organisation: null,
};



/** WEBPACK FOOTER **
 ** ./src/components/duplicate-campaign-modal/index.js
 **/